import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime, DOC_TYPE_LABELS } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  FileText, Package, Receipt, Users, ArrowRight,
  Plus, Clock, TrendingUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function StatCard({ icon: Icon, label, draftCount, issuedCount, color, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="card-surface p-5 text-left hover:shadow-md transition-shadow group"
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
      </div>
      <p className="text-2xl font-bold font-['Outfit'] tracking-tight">{draftCount + issuedCount}</p>
      <p className="text-xs text-zinc-500 mt-1">{label}</p>
      <div className="flex gap-3 mt-2">
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{draftCount} bozze</span>
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{issuedCount} emessi</span>
      </div>
    </button>
  );
}

function SimpleStatCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <button onClick={onClick} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="card-surface p-5 text-left hover:shadow-md transition-shadow group">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></div>
        <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
      </div>
      <p className="text-2xl font-bold font-['Outfit'] tracking-tight">{value}</p>
      <p className="text-xs text-zinc-500 mt-1">{label}</p>
    </button>
  );
}

const DOC_ICONS = { quote: FileText, inventory: Package, proforma: Receipt };
const DOC_ROUTES = { quote: "quotes", inventory: "inventories", proforma: "proformas" };

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canViewAll = hasPermission("quotes.edit_all");
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [sRes, aRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/recent-activity"),
        ]);
        setStats(sRes.data);
        setActivity(aRes.data.items || []);
      } catch {
        toast.error("Errore nel caricamento della dashboard");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tighter font-['Outfit']">
          Ciao, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Ecco un riepilogo della tua attività</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Preventivi"
          draftCount={stats.quotes_draft} issuedCount={stats.quotes_issued}
          color="bg-blue-50 text-[#002FA7]" onClick={() => navigate("/quotes")} />
        <StatCard icon={Package} label="Inventari"
          draftCount={stats.inventories_draft} issuedCount={stats.inventories_issued}
          color="bg-emerald-50 text-emerald-700" onClick={() => navigate("/inventories")} />
        {canViewAll && (
          <>
            <StatCard icon={Receipt} label="Proforma"
              draftCount={stats.proformas_draft} issuedCount={stats.proformas_issued}
              color="bg-amber-50 text-amber-700" onClick={() => navigate("/proformas")} />
            <SimpleStatCard icon={Users} label="Clienti" value={stats.customers}
              color="bg-violet-50 text-violet-700" onClick={() => navigate("/customers")} />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 card-surface">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-200">
            <Clock className="w-4 h-4 text-zinc-400" />
            <h2 className="font-semibold text-sm font-['Outfit']">Attività Recente</h2>
          </div>
          <div className="divide-y divide-zinc-100">
            {activity.map((item) => {
              const DocIcon = DOC_ICONS[item.doc_type] || FileText;
              const route = DOC_ROUTES[item.doc_type] || "quotes";
              return (
                <button key={`${item.doc_type}-${item.id}`}
                  onClick={() => navigate(`/${route}/${item.id}`)}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-zinc-50/50 transition-colors text-left"
                  data-testid={`activity-${item.id}`}>
                  <DocIcon className="w-4 h-4 text-zinc-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono truncate">{item.number || "BOZZA"}</span>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-xs text-zinc-500 truncate">
                      {DOC_TYPE_LABELS[item.doc_type] || item.doc_type}
                      {item.customer_name ? ` · ${item.customer_name}` : ""}
                      {item.subject ? ` · ${item.subject}` : ""}
                    </p>
                  </div>
                  <span className="text-[10px] text-zinc-400 shrink-0">{formatDateTime(item.updated_at)}</span>
                </button>
              );
            })}
            {activity.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-zinc-400">Nessuna attività recente</div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        {canViewAll && (
          <div className="card-surface">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-200">
              <TrendingUp className="w-4 h-4 text-zinc-400" />
              <h2 className="font-semibold text-sm font-['Outfit']">Azioni Rapide</h2>
            </div>
            <div className="p-4 space-y-2">
              <Button variant="outline" className="w-full justify-start gap-2 rounded-sm h-10"
                data-testid="quick-new-quote"
                onClick={() => navigate("/quotes/new")}>
                <FileText className="w-4 h-4 text-[#002FA7]" /> Nuovo Preventivo
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 rounded-sm h-10"
                data-testid="quick-new-inventory"
                onClick={() => navigate("/inventories/new")}>
                <Package className="w-4 h-4 text-emerald-600" /> Nuovo Inventario
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 rounded-sm h-10"
                data-testid="quick-new-proforma"
                onClick={() => navigate("/proformas/new")}>
                <Receipt className="w-4 h-4 text-amber-600" /> Nuova Proforma
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 rounded-sm h-10"
                data-testid="quick-new-customer"
                onClick={() => navigate("/customers")}>
                <Users className="w-4 h-4 text-violet-600" /> Gestisci Clienti
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
